import React, { useState } from 'react';
import { Button, CircularProgress, Alert, Box } from '@mui/material';
import axios from '../utils/axiosConfig';

interface ImageUploadProps {
  entityId: string;
  entityType: 'Club' | 'Competition' | 'Athlete' | 'Judge' | 'Official';
  onSuccess?: () => void;
  label?: string;
  imageType?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ 
  entityId, 
  entityType,
  onSuccess,
  label = 'Bild',
  imageType = 'default',
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<string | null>(null);

  // Prüft Bildabmessungen
  const checkImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const maxDimension = 1024;
        const isValidSize = img.width <= maxDimension && img.height <= maxDimension;
        URL.revokeObjectURL(img.src); // Speicher freigeben
        resolve(isValidSize);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      if (!['image/jpeg', 'image/png'].includes(selectedFile.type)) {
        setError('Nur JPG und PNG Dateien sind erlaubt');
        setFile(null);
        return;
      }
      
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('Datei darf nicht größer als 5MB sein');
        setFile(null);
        return;
      }
      
      // 3. Bildabmessungen prüfen
      const isValidDimensions = await checkImageDimensions(selectedFile);
      if (!isValidDimensions) {
        setError('Bildabmessungen dürfen nicht größer als 1024x1024 Pixel sein');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError('');
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      await axios.post(`/api/images/${entityType}/${entityId}/${imageType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      setFile(null);
      setPreview(null);
    } catch (err: any) {
      setError(err.response?.data || 'Upload fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {preview && (
        <Box sx={{ mb: 2 }}>
          <img 
            src={preview} 
            alt="Vorschau" 
            style={{ maxWidth: '200px', maxHeight: '200px' }} 
          />
        </Box>
      )}
      
      <input
        accept="image/jpeg,image/png"
        style={{ display: 'none' }}
        id={`${entityType}-${entityId}-upload`}
        type="file"
        onChange={handleFileChange}
      />
      
      <label htmlFor={`${entityType}-${entityId}-upload`}>
        <Button variant="contained" component="span">
          {label} auswählen
        </Button>
      </label>
      
      {file && (
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleUpload}
          disabled={loading}
          sx={{ ml: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Upload'}
        </Button>
      )}
    </Box>
  );
};

export default ImageUpload;